import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  Snackbar,
  Alert,
  Paper,
  CircularProgress,
} from "@mui/material";
import axios from '../../Configs/axiosInstance';
import { useNavigate, useParams } from "react-router-dom";
import { MAIN_URL } from "../../Configs/Urls";

import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';
import PatientFormFields, { emptyPatientForm, validatePatientForm } from "./Components/PatientFormFields";

export default function PatientFormPage() {
  const { id: patientId } = useParams();
  const navigate = useNavigate();

  const [patientData, setPatientData] = useState(emptyPatientForm);
  const [errors, setErrors] = useState({});
  const [fetching, setFetching] = useState(true);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (patientId) {
      setFetching(true);
      axios
        .get(`${MAIN_URL}patients/${patientId}`)
        .then((response) => {
          setPatientData(response.data);
        })
        .catch(() => {
          setSnackbarMessage("Error fetching patient data.");
          setSnackbarSeverity("error");
          setOpenSnackbar(true);
        })
        .finally(() => setFetching(false));
    }
  }, [patientId]);

  const handleInputChange = (event) => {
    setErrors({});
    const { name, value } = event.target;
    setPatientData((prevData) => ({ ...prevData, [name]: value }));
  };

  const setField = (field) => (value) => {
    setErrors({});
    setPatientData((prevData) => ({ ...prevData, [field]: value }));
  };

  const setMultiField = (field) => (values) => {
    setErrors({});
    setPatientData((prevData) => ({ ...prevData, [field]: values }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const formErrors = validatePatientForm(patientData);
    setErrors(formErrors);

    if (Object.keys(formErrors).length === 0) {
      setLoading(true);

      axios.put(`${MAIN_URL}patients/${patientId}`, patientData)
        .then(() => {
          setSnackbarMessage("Patient updated successfully!");
          setSnackbarSeverity("success");
          setOpenSnackbar(true);

          setTimeout(() => {
            navigate(`/patients/${patientId}/view`);
          }, 1200);
          setLoading(false);
        })
        .catch((error) => {
          setSnackbarMessage(error?.response?.data?.message ? error?.response.data.message : "Error submitting form. Please try again.");
          setSnackbarSeverity("error");
          setOpenSnackbar(true);
          console.error("Error submitting form:", error);
          setLoading(false);
        });
    } else {
      setSnackbarMessage("Form Not Filled Properly, Please Check and Try Again");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  if (fetching) {
    return (
      <Box sx={{ width: "100%", display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 960 }}>
      <Button
        startIcon={<ArrowBackRoundedIcon />}
        onClick={() => navigate('/patients')}
        sx={{ mb: 2, textTransform: 'none', fontWeight: 600 }}
      >
        Back to Patients List
      </Button>

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <PatientFormFields
            patientData={patientData}
            errors={errors}
            onInputChange={handleInputChange}
            onChoiceChange={setField}
            onMultiChoiceChange={setMultiField}
          />

          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, sm: 2.5 },
              borderRadius: 3,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
              <NotesRoundedIcon fontSize="small" />
              <Typography variant="body2">
                Double-check the details above before saving.
              </Typography>
            </Stack>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <EditRoundedIcon />}
              sx={{ borderRadius: 2, px: 4, textTransform: 'none', fontWeight: 600 }}
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </Paper>
        </Stack>
      </form>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} variant="filled" sx={{ fontWeight: 600 }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
